import { DomainRuleError } from "../errors.js";
import { assertCurrency, minorUnits, pow10 } from "./currency.js";
import { divideRounded } from "./rounding.js";
const INTEGER = /^-?(0|[1-9]\d*)$/;
/** An amount in integer minor units of one currency. Immutable; arithmetic never uses floating point. */
export class Money {
    minor;
    currency;
    constructor(minor, currency) {
        this.minor = minor;
        this.currency = currency;
        Object.freeze(this);
    }
    static of(minor, currency) {
        return new Money(minor, assertCurrency(currency));
    }
    static zero(currency) {
        return Money.of(0n, currency);
    }
    /** Parses the API shape `{ amountMinor: "1250000", currency: "SGD" }`. */
    static fromApi(value) {
        if (!INTEGER.test(value.amountMinor)) {
            throw new DomainRuleError("VALIDATION_FAILED", `amountMinor must be an integer string, got "${value.amountMinor}"`);
        }
        return Money.of(BigInt(value.amountMinor), value.currency);
    }
    toApi() {
        return { amountMinor: this.minor.toString(), currency: this.currency };
    }
    add(other) {
        this.assertSameCurrency(other);
        return new Money(this.minor + other.minor, this.currency);
    }
    subtract(other) {
        this.assertSameCurrency(other);
        return new Money(this.minor - other.minor, this.currency);
    }
    negate() {
        return new Money(-this.minor, this.currency);
    }
    isZero() {
        return this.minor === 0n;
    }
    isNegative() {
        return this.minor < 0n;
    }
    isPositive() {
        return this.minor > 0n;
    }
    compare(other) {
        this.assertSameCurrency(other);
        if (this.minor < other.minor)
            return -1;
        if (this.minor > other.minor)
            return 1;
        return 0;
    }
    equals(other) {
        return this.currency === other.currency && this.minor === other.minor;
    }
    /** Multiplies by numerator / denominator with explicit rounding. */
    multiplyFraction(numerator, denominator, mode = "half-even") {
        return new Money(divideRounded(this.minor * numerator, denominator, mode), this.currency);
    }
    /** Applies a rate in basis points (250 = 2.5%). */
    percentBps(basisPoints, mode = "half-even") {
        return this.multiplyFraction(basisPoints, 10000n, mode);
    }
    /**
     * Splits the amount across weights without losing or creating a minor unit.
     * Remainders go to the parts with the largest fractional share, earlier parts first on ties.
     */
    allocate(weights) {
        if (this.minor < 0n)
            throw new DomainRuleError("VALIDATION_FAILED", "Cannot allocate a negative amount");
        if (weights.length === 0)
            throw new DomainRuleError("VALIDATION_FAILED", "At least one weight is required");
        if (weights.some((weight) => weight < 0n))
            throw new DomainRuleError("VALIDATION_FAILED", "Weights must not be negative");
        const total = weights.reduce((sum, weight) => sum + weight, 0n);
        if (total === 0n)
            throw new DomainRuleError("VALIDATION_FAILED", "Weights must not all be zero");
        const shares = weights.map((weight, index) => ({
            index,
            floor: (this.minor * weight) / total,
            remainder: (this.minor * weight) % total,
        }));
        let left = this.minor - shares.reduce((sum, share) => sum + share.floor, 0n);
        const byRemainder = [...shares].sort((a, b) => a.remainder === b.remainder ? a.index - b.index : a.remainder > b.remainder ? -1 : 1);
        const extra = new Map();
        for (const share of byRemainder) {
            if (left === 0n)
                break;
            extra.set(share.index, 1n);
            left -= 1n;
        }
        return shares.map((share) => new Money(share.floor + (extra.get(share.index) ?? 0n), this.currency));
    }
    static min(a, b) {
        return a.compare(b) <= 0 ? a : b;
    }
    static max(a, b) {
        return a.compare(b) >= 0 ? a : b;
    }
    static sum(currency, amounts) {
        return amounts.reduce((total, amount) => total.add(amount), Money.zero(currency));
    }
    /** Human-readable form for logs and tests only; clients format with Intl. */
    toString() {
        const digits = minorUnits(this.currency);
        const negative = this.minor < 0n;
        const absolute = negative ? -this.minor : this.minor;
        const scale = pow10(digits);
        const whole = absolute / scale;
        const fraction = digits === 0 ? "" : `.${(absolute % scale).toString().padStart(digits, "0")}`;
        return `${this.currency} ${negative ? "-" : ""}${whole.toString()}${fraction}`;
    }
    assertSameCurrency(other) {
        if (other.currency !== this.currency) {
            throw new DomainRuleError("VALIDATION_FAILED", `Currency mismatch: ${this.currency} and ${other.currency}`);
        }
    }
}
//# sourceMappingURL=money.js.map